import type { MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Button, VisuallyHidden } from '@mantine/core';
import { formatMoney } from '@/lib/format';
import type { BestPriceAction, BestPriceTone } from './BestPriceLabel';

interface BestPriceButtonProps {
  /** Страница встречного предложения с действием «Купить»/«Продать». */
  readonly to: string;
  readonly action: BestPriceAction;
  /** `gain` — выгодная или лучшая в поиске цена: кнопка залита; иначе — белая с рамкой. */
  readonly tone?: BestPriceTone;
  /** Цена для доступного имени ссылки: «Продать по 16 439 ₽/т», хотя видна только «Продать». */
  readonly price?: number;
  /** Чем эта кнопка отличается от соседних (строка качества, предоплата) — дописывается к доступному имени. */
  readonly context?: string;
}

// Кнопка действия по лучшей цене: «Продать» или «Купить». Залита только у выгодной цены, к которой побуждаем.
// Поднята над растянутой ссылкой-оверлеем карточки (zIndex 2 + stopPropagation); в обычном потоке безвредно.
export default function BestPriceButton({ to, action, tone = 'plain', price, context }: BestPriceButtonProps) {
  const { t } = useTranslation();
  const hasPrice = price != null && Number.isFinite(price);

  return (
    <Button
      component={Link}
      to={to}
      variant={tone === 'gain' ? 'filled' : 'default'}
      px={22}
      style={{ position: 'relative', zIndex: 2, flexShrink: 0 }}
      onClick={(event: MouseEvent) => event.stopPropagation()}
    >
      {t(action === 'sell' ? 'app.bestPrice.sell' : 'app.bestPrice.buy')}
      {hasPrice ? <VisuallyHidden> {t('app.bestPrice.forPrice', { price: formatMoney(price) })}</VisuallyHidden> : null}
      {context ? <VisuallyHidden>, {context}</VisuallyHidden> : null}
    </Button>
  );
}
